import React, { useLayoutEffect, useRef, useEffect, useState } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5percent from '@amcharts/amcharts5/percent';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';
import FullScreenIcon from '../../../features/common/FullScreenIcon/FullScreenIcon';
import DownloadChart from '../../../components/time-series-chart/download/downloadChart';
import { exportChart } from '../../../utilities/ExportChart';

interface ChartData {
  category: string;
  value: number;
  color: string;
}

interface PieChartProps {
  data: ChartData[];
  showHeader?: boolean;
  title?: string;
}

export default function PieChart({ data, showHeader = true, title = 'Pie Chart' }: PieChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const chartId = `pie-chart-content-${title.replace(/\s+/g, '').toLowerCase()}`;

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const handleChartDownload = async (option: string) => {
    const element = document.getElementById(chartId);
    const filename = `${title.replace(/\s+/g, '')}-PieChart`;
    if (!element) return;
    await exportChart(element, getExportType(option), filename, undefined, undefined, title);
  };

  const getExportType = (option: string): 'JPG' | 'PDF' => (option === 'Download as PDF' ? 'PDF' : 'JPG');

  useLayoutEffect(() => {
    if (!chartRef.current) return;

    // Set base dimensions
    chartRef.current.style.width = '100%';
    chartRef.current.style.height = '100%';

    // Set minimum dimensions for normal mode (not fullscreen)
    if (!isFullscreen) {
      chartRef.current.style.minHeight = '170px';
      chartRef.current.style.minWidth = '170px';
    }

    const root = am5.Root.new(chartRef.current);

    root.setThemes([am5themes_Dark.new(root)]);

    const chart = root.container.children.push(
      am5percent.PieChart.new(root, {
        layout: root.verticalLayout,
        innerRadius: am5.percent(60),
      }),
    );

    const series = chart.series.push(
      am5percent.PieSeries.new(root, {
        valueField: 'value',
        categoryField: 'category',
        alignLabels: false,
      }),
    );

    series.labels.template.setAll({
      textType: 'circular',
      centerX: 0,
      centerY: 0,
      visible: false,
    });

    series.ticks.template.setAll({
      visible: false,
    });

    series.slices.template.setAll({
      strokeOpacity: 1,
      strokeWidth: 1.5,
      stroke: am5.color('#fff'),
    });

    // Set the data first
    series.data.setAll(data);

    // Apply colors using the data context directly
    series.slices.template.adapters.add('fill', (fill, target) => {
      const dataItem = target.dataItem;
      if (dataItem) {
        const itemData = dataItem.dataContext as ChartData;
        if (itemData && itemData.color) {
          return am5.color(itemData.color);
        }
      }
      return fill;
    });

    // Alternative approach: Set colors directly after data is processed
    series.events.once('datavalidated', () => {
      series.slices.each((slice, index) => {
        if (data[index] && data[index].color) {
          slice.set('fill', am5.color(data[index].color));
        }
      });
    });

    series.appear(1000, 100);

    return () => {
      root.dispose();
    };
  }, [data, isFullscreen]);

  return (
    <section
      ref={containerRef}
      className={`pie-chart-container ${isFullscreen ? 'fullscreen' : ''} ${!showHeader ? 'no-header' : ''}`}
    >
      {showHeader && (
        <>
          <header className='ogl-chart-header'>
            <div className='chart-title'>
              <h2>{title}</h2>
            </div>
            <div className='chart-actions'>
              <button
                analytics-id='pie-chart-fullscreen-btn'
                className='chart-action-button'
                onClick={toggleFullscreen}
              >
                <FullScreenIcon />
              </button>
              <button analytics-id='pie-chart-download-btn' className='chart-action-button'>
                <DownloadChart
                  onClick={(option) => handleChartDownload(option)}
                  canExportJpg={true}
                  canExportPdf={true}
                />
              </button>
            </div>
          </header>
          <div className='chart-divider'></div>
        </>
      )}
      <div id={chartId}>
        <div
          ref={chartRef}
          className='pie-chart-canvas'
          style={{ width: '100%', height: '100%', minHeight: isFullscreen ? '700px' : '' }}
        />
      </div>
    </section>
  );
}
